import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ActiveEnvironmentComponent, OrganizationVo } from '../common/services/application.do';
import { EnvironmentComponentsService, AuthorizationService } from '../common/services/application.service';
import { environment } from '../../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { ApplicationContextService } from '../common/services/application-context.service';
import { IUserTile, getUserInitials, getRandomColor } from '../management/newversion/users/user.functions';
import { EUserTileSize } from '../management/newversion/users/user-tile/user-tile.component';

type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

@Component({
  selector: 'app-applications',
  templateUrl: './applications.component.html',
  styleUrls: ['./applications.component.scss'],
  standalone: false
})
export class ApplicationsComponent implements OnInit, OnDestroy {
  greeting: string;
  currentDate: string;
  applications: ActiveEnvironmentComponent[];
  filteredApplications: ActiveEnvironmentComponent[];
  account: AccountInfo;
  hasManagementAccess: boolean;
  userTile: IUserTile;
  userTileSize = EUserTileSize.Medium;
  loading = true;
  searchTerm = '';
  portalUrl: string;
  organizationName: string;

  private orgSub: Subscription;

  constructor(
    private environmentComponentsService: EnvironmentComponentsService,
    private authService: MsalService,
    private authorizationService: AuthorizationService,
    private applicationContextService: ApplicationContextService
  ) { }

  ngOnInit(): void {
    this.currentDate = this.formatCurrentDate();

    // Tiles, management access and the greeting are all scoped to the resolved
    // organization and the active account. A home-directory sign-in switches
    // directory — and therefore token — after this component has initialised,
    // so load on every organization emission rather than once here.
    this.orgSub = this.applicationContextService.currentOrganization$
      .pipe(filter(org => !!org))
      .subscribe(org => this.show(org));
  }

  ngOnDestroy(): void {
    this.orgSub?.unsubscribe();
  }

  // An organization with its own portal has moved, so this page hands the person over
  // instead of serving tiles and a Manager link from an address that is no longer theirs.
  // Neither request load() makes is worth firing, because nothing will show their results.
  private show(organization: OrganizationVo): void {
    this.account = this.authService.instance.getActiveAccount();
    this.greeting = this.getGreeting(this.account.name);
    this.userTile = this.createUserTile();

    this.organizationName = organization.name;
    this.portalUrl = organization.portalUrl;

    if (this.portalUrl) {
      this.loading = false;
      return;
    }

    this.load();
  }

  private load(): void {
    this.loading = true;
    this.environmentComponentsService.getAuthorizedActives().subscribe(data => {
      this.applications = data;
      this.filteredApplications = data;
      this.loading = false;
    });
    this.authorizationService.hasManagementAccess().subscribe(res => {
      this.hasManagementAccess = res;
    });
  }

  private createUserTile(): IUserTile {
    return {
      text: getUserInitials(this.account.name),
      color: getRandomColor(this.account.name),
      title: this.account.name,
      hidden: false
    };
  }

  getIsDevelopment() {
    return environment.production === false;
  }

  onClick(app: ActiveEnvironmentComponent) {
    window.open(app.websiteUrl, '_blank');
  }

  getGreeting(userName: string): string {
    const hour = new Date().getHours();
    let timeOfDay: TimeOfDay;

    if (hour >= 5 && hour < 12) {
      timeOfDay = 'morning';
    } else if (hour >= 12 && hour < 17) {
      timeOfDay = 'afternoon';
    } else if (hour >= 17 && hour < 21) {
      timeOfDay = 'evening';
    } else {
      timeOfDay = 'night';
    }

    return `Good ${timeOfDay}, ${userName}!`;
  }

  formatCurrentDate(): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    };
    return now.toLocaleDateString('en-US', options);
  }

  onSearch(): void {
    if (!this.searchTerm.trim()) {
      this.filteredApplications = this.applications;
      return;
    }

    const term = this.searchTerm.toLowerCase().trim();
    this.filteredApplications = this.applications.filter(app =>
      app.name.toLowerCase().includes(term) ||
      app.description?.toLowerCase().includes(term) ||
      app.environment?.toLowerCase().includes(term)
    );
  }

  onLogoutClicked() {
    this.authService.logout({
      account: this.account
    });
  }
}
